/**
 * Plan Definitions and Feature Limits
 *
 * Defines all subscription plans and their associated features/limits.
 * UI/pricing display layer — numeric limits (seats, valuations/year) derive
 * from PLAN_LIMITS in billing/entitlements-helpers.ts (single source of
 * truth); do not hardcode them here. Display strings stay in this file.
 */

import { PLAN_LIMITS } from '@/lib/platform/billing/entitlements-helpers';

export type PlanTier = 'starter' | 'professional' | 'enterprise';

export interface PlanFeatures {
  // Team Management
  includedSeats: number;
  canPurchaseAdditionalSeats: boolean;
  additionalSeatPrice: number; // kr/month per additional seat

  // Core Features
  canCreateValuations: boolean;
  canExportData: boolean;
  canUseAPI: boolean;

  // Advanced Features
  hasAdvancedReports: boolean; // Reports page in menu (Professional and Enterprise)
  hasAuditLog: boolean; // Audit Log page (Enterprise only)
  hasGranularPermissions: boolean; // Edit Valuations permissions (Enterprise only)
  hasPlayerSalesTracking: boolean; // Player Sales Tracking (Professional and Enterprise)
  sponsorshipSponsors: boolean; // Sponsorships page (Professional and Enterprise)
  sponsorshipPackages: boolean; // Packages + Item Library pages (Enterprise only)
  documents: boolean; // Documents page (Professional and Enterprise only)
  hasPrioritySupport: boolean;

  // Add-on Features (can be purchased separately or included)
  hasAIInsights: boolean; // AI Insights add-on (Professional add-on, Enterprise included)
  hasSankeyChart: boolean; // Sankey Chart add-on (Starter/Professional add-on, Enterprise included)

  // Limits (tier-based, NOT frequency-based)
  valuationsPerYear: number; // Number of valuations per year based on plan tier
  maxAPICallsPerMonth: number | null;
}

export interface Plan {
  id: PlanTier;
  name: string;
  description: string;
  pricing: {
    monthly: number;
    annually: number;
  };
  features: PlanFeatures;
  popular?: boolean;
}

/**
 * Plan Definitions
 */
export const PLANS: Record<PlanTier, Plan> = {
  starter: {
    id: 'starter',
    name: 'Starter',
    description: 'Perfect for small clubs getting started with valuations',
    pricing: {
      monthly: 0, // Fallback only - always fetch from Stripe
      annually: 0, // Fallback only - always fetch from Stripe
    },
    features: {
      // Team Management
      includedSeats: PLAN_LIMITS.starter.maxUsers, // 1
      canPurchaseAdditionalSeats: true,
      additionalSeatPrice: 990, // kr/month per additional seat

      // Core Features
      canCreateValuations: true,
      canExportData: true,
      canUseAPI: false,

      // Advanced Features
      hasAdvancedReports: false, // Reports page hidden
      hasAuditLog: false, // Audit Log page hidden
      hasGranularPermissions: false, // Edit Valuations permissions hidden (always ON)
      hasPlayerSalesTracking: false, // Player Sales Tracking hidden (Professional/Enterprise only)
      sponsorshipSponsors: false, // Sponsorships page hidden (Professional/Enterprise only)
      sponsorshipPackages: false, // Packages + Item Library pages hidden (Enterprise only)
      documents: false, // Documents page hidden (Professional/Enterprise only)
      hasPrioritySupport: false,

      // Add-on Features
      hasAIInsights: false, // Not available (requires Reports access)
      hasSankeyChart: false, // Can be purchased as add-on

      // Limits
      valuationsPerYear: PLAN_LIMITS.starter.maxValuations, // 12/year (Monthly)
      maxAPICallsPerMonth: null,
    },
  },

  professional: {
    id: 'professional',
    name: 'Professional',
    description: 'For growing organizations with advanced needs',
    pricing: {
      monthly: 0, // Fallback only - always fetch from Stripe
      annually: 0, // Fallback only - always fetch from Stripe
    },
    features: {
      // Team Management
      includedSeats: PLAN_LIMITS.professional.maxUsers, // 3
      canPurchaseAdditionalSeats: true,
      additionalSeatPrice: 990, // kr/month per additional seat

      // Core Features
      canCreateValuations: true,
      canExportData: true,
      canUseAPI: false, // Changed: Professional does NOT have API

      // Advanced Features
      hasAdvancedReports: true, // Professional has Reports page
      hasAuditLog: false, // Audit Log page hidden
      hasGranularPermissions: false, // Edit Valuations permissions hidden (always ON)
      hasPlayerSalesTracking: true, // Player Sales Tracking available (Professional/Enterprise)
      sponsorshipSponsors: true, // Sponsorships page available (Professional/Enterprise)
      sponsorshipPackages: false, // Packages + Item Library pages hidden (Enterprise only)
      documents: true, // Documents page available (Professional/Enterprise)
      hasPrioritySupport: false,

      // Add-on Features
      hasAIInsights: false, // Can be purchased as add-on (300 kr/month)
      hasSankeyChart: false, // Can be purchased as add-on (200 kr/month)

      // Limits
      valuationsPerYear: PLAN_LIMITS.professional.maxValuations, // 52/year (Weekly)
      maxAPICallsPerMonth: null, // No API access
    },
  },

  enterprise: {
    id: 'enterprise',
    name: 'Enterprise',
    description: 'Unlimited power for large organizations',
    pricing: {
      monthly: 0, // Fallback only - always fetch from Stripe
      annually: 0, // Fallback only - always fetch from Stripe
    },
    features: {
      // Team Management
      includedSeats: PLAN_LIMITS.enterprise.maxUsers, // 9
      canPurchaseAdditionalSeats: true,
      additionalSeatPrice: 990, // kr/month per additional seat

      // Core Features
      canCreateValuations: true,
      canExportData: true,
      canUseAPI: true,

      // Advanced Features
      hasAdvancedReports: true, // Reports page available
      hasAuditLog: true, // Audit Log page available
      hasGranularPermissions: true, // Edit Valuations permissions can be controlled granularly
      hasPlayerSalesTracking: true, // Player Sales Tracking available (Professional/Enterprise)
      sponsorshipSponsors: true, // Sponsorships page available (Professional/Enterprise)
      sponsorshipPackages: true, // Packages + Item Library pages available (Enterprise only)
      documents: true, // Documents page available (Professional/Enterprise)
      hasPrioritySupport: true,

      // Add-on Features
      hasAIInsights: true, // Included in Enterprise
      hasSankeyChart: true, // Included in Enterprise

      // Limits
      valuationsPerYear: PLAN_LIMITS.enterprise.maxValuations, // Unlimited (-1 sentinel)
      maxAPICallsPerMonth: 100000,
    },
  },
};

/**
 * Get plan by tier
 */
export function getPlan(tier: PlanTier): Plan {
  return PLANS[tier];
}

/**
 * Get all plans as array
 */
export function getAllPlans(): Plan[] {
  return Object.values(PLANS);
}

/**
 * Check if a feature is available for a plan
 */
export function hasFeature(tier: PlanTier, feature: keyof PlanFeatures): boolean {
  const plan = getPlan(tier);
  return !!plan.features[feature];
}

/**
 * Get formatted price
 */
export function formatPrice(amount: number, period: 'monthly' | 'annually' = 'monthly'): string {
  if (period === 'monthly') {
    return `${amount.toLocaleString('da-DK')} kr/month`;
  }
  const monthlyEquivalent = Math.round(amount / 12);
  return `${amount.toLocaleString('da-DK')} kr/year (${monthlyEquivalent.toLocaleString('da-DK')} kr/month)`;
}

/**
 * Calculate discount percentage
 */
export function getAnnualDiscount(tier: PlanTier): number {
  const plan = getPlan(tier);
  const monthlyTotal = plan.pricing.monthly * 12;
  const discount = ((monthlyTotal - plan.pricing.annually) / monthlyTotal) * 100;
  return Math.round(discount);
}

/**
 * Add-on Definitions
 */
export type AddOnId = 'ai-insights' | 'sankey';

export interface AddOn {
  id: AddOnId;
  name: string;
  description: string;
  pricing: {
    monthly: number;
    annually: number;
  };
}

export const ADD_ONS: Record<AddOnId, AddOn> = {
  'ai-insights': {
    id: 'ai-insights',
    name: 'AI Insights',
    description: 'Get AI-powered insights and recommendations for your valuations',
    pricing: {
      monthly: 300,
      annually: 3000, // ~250 kr/month (17% discount)
    },
  },
  sankey: {
    id: 'sankey',
    name: 'Sankey Chart',
    description: 'Visualize cash flow with interactive Sankey diagrams',
    pricing: {
      monthly: 200,
      annually: 2000, // ~167 kr/month (17% discount)
    },
  },
};

/**
 * Subscription Period
 * NOTE: Valuation limits are PLAN-based (not period-based) — see PLAN_LIMITS
 * in billing/entitlements-helpers.ts (12/year Starter, 52/year Professional,
 * unlimited Enterprise).
 */
export type SubscriptionPeriod = 'monthly' | 'annually';

/**
 * Get add-on by ID
 */
export function getAddOn(id: AddOnId): AddOn {
  return ADD_ONS[id];
}

/**
 * Get all add-ons as array
 */
export function getAllAddOns(): AddOn[] {
  return Object.values(ADD_ONS);
}

/**
 * Check if user has purchased an add-on
 * Handles multiple naming formats for backwards compatibility:
 * - 'ai-insights' matches: 'ai-insights', 'AI Insights', 'AI-Insights'
 * - 'sankey' matches: 'sankey', 'sankey-chart', 'Sankey-Chart', 'Sankey Chart'
 */
export function hasAddOn(purchasedAddOns: string[], addOnId: AddOnId): boolean {
  // Normalize purchased add-ons to lowercase kebab-case for comparison
  const normalizedAddOns = purchasedAddOns.map((addon) => addon.toLowerCase().replace(/\s+/g, '-'));

  // Check exact match after normalization
  if (normalizedAddOns.includes(addOnId)) {
    return true;
  }

  // Handle backwards compatibility for sankey naming variations
  if (addOnId === 'sankey') {
    return normalizedAddOns.some((addon) => addon === 'sankey' || addon === 'sankey-chart');
  }

  // Handle backwards compatibility for ai-insights naming variations
  if (addOnId === 'ai-insights') {
    return normalizedAddOns.some((addon) => addon === 'ai-insights' || addon === 'ai-insight');
  }

  return false;
}
